/** @jsxImportSource react */
import {renderToStaticMarkup} from 'react-dom/server';

import type {StockTakeReport} from '../types/StockTakeReport';
import type {StockTakeReportMetric} from '../types/StockTakeReportMetric';

// Standalone print HTML cannot depend on the application's theme or runtime styles.
const DOCUMENT_STYLES = `
  @page { size: A4 portrait; margin: 12mm 12mm 16mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #172b4d; font: .75rem/1.45 Arial, sans-serif; }
  header { border-bottom: .125rem solid #0065ff; padding-bottom: .75rem; font-weight: bold; }
  h1 { font-size: 1.5rem; margin: 1rem 0 .5rem; }
  h2 { font-size: 1.125rem; border-bottom: .0625rem solid #dfe3ea; padding-bottom: .5rem; }
  h3 { font-size: .875rem; margin: 1rem 0 .5rem; }
  p, small { color: #5d6574; }
  main > section + section { break-before: page; }
  article { break-inside: avoid; }
  table { border-collapse: collapse; width: 100%; }
  tr { break-inside: avoid; }
  th, td { padding: .375rem .5rem; border-bottom: .0625rem solid #e4e8ef; vertical-align: top; }
  th { width: 65%; text-align: left; font-weight: normal; }
  td { text-align: right; font-weight: bold; }
  small { display: block; font-weight: normal; white-space: pre-line; }
  footer { border-top: .0625rem solid #dfe3ea; margin-top: 1.25rem; padding-top: .5rem; }

  body[data-report-kind="daily"] { line-height: 1.35; }
  body[data-report-kind="daily"] header { padding-bottom: .5rem; }
  body[data-report-kind="daily"] h1 { font-size: 1.25rem; margin: .75rem 0 .25rem; }
  body[data-report-kind="daily"] h2 { margin: 1rem 0 .75rem; padding-bottom: .375rem; }
  body[data-report-kind="daily"] h3 { margin: 0 0 .5rem; }
  body[data-report-kind="daily"] [data-document-metadata] tbody {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: .5rem;
  }
  body[data-report-kind="daily"] [data-document-metadata] tr {
    display: flex;
    flex-direction: column;
    padding: .5rem;
    border: .0625rem solid #dfe3ea;
    border-radius: .25rem;
    background: #f8f9fb;
  }
  body[data-report-kind="daily"] [data-document-metadata] th, body[data-report-kind="daily"] [data-document-metadata] td {
    display: block;
    width: auto;
    padding: 0;
    border: 0;
    text-align: left;
    overflow-wrap: anywhere;
  }
  body[data-report-kind="daily"] [data-document-metadata] th { color: #5d6574; font-size: .6875rem; }
  body[data-report-kind="daily"] [data-document-metadata] td { margin-top: .25rem; }
  body[data-report-kind="daily"] [data-report-groups] {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: .75rem;
    break-inside: avoid;
  }
  body[data-report-kind="daily"] [data-report-group] {
    padding: .75rem;
    border: .0625rem solid #dfe3ea;
    border-radius: .25rem;
  }
  body[data-report-kind="daily"] [data-report-group][data-group="values"],
  body[data-report-kind="daily"] [data-report-group][data-group="corrections"] { grid-column: 1 / -1; }
  body[data-report-kind="daily"] [data-report-group][data-group="values"] tbody {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: .75rem;
  }
  body[data-report-kind="daily"] [data-report-group][data-group="values"] tr { display: flex; flex-direction: column; }
  body[data-report-kind="daily"] [data-report-group][data-group="values"] th,
  body[data-report-kind="daily"] [data-report-group][data-group="values"] td {
    display: block;
    width: auto;
    padding: 0;
    border: 0;
    text-align: left;
  }
  body[data-report-kind="daily"] [data-report-group][data-group="values"] td { margin-top: .25rem; font-size: 1rem; }
  body[data-report-kind="daily"] [data-report-group][data-group="values"] small { font-size: .6875rem; }
  body[data-report-kind="daily"] [data-report-group][data-group="receipts"] { border-left: .1875rem solid #24a148; }
  body[data-report-kind="daily"] [data-report-group][data-group="issues"] { border-left: .1875rem solid #e53935; }
  body[data-report-kind="daily"] [data-report-group][data-group="corrections"] { border-left: .1875rem solid #0065ff; }
  body[data-report-kind="daily"] [data-report-group] th { width: 55%; }
  body[data-report-kind="daily"] [data-report-group] th, body[data-report-kind="daily"] [data-report-group] td { padding: .375rem .25rem; }
  body[data-report-kind="daily"] [data-report-group] tr:last-child th,
  body[data-report-kind="daily"] [data-report-group] tr:last-child td { border-bottom: 0; }
  body[data-report-kind="daily"] [data-report-group] tr:last-child th { font-weight: bold; }
  body[data-report-kind="daily"] footer { margin-top: 1rem; }
`;

// eslint-disable-next-line react/only-export-components -- This module serializes standalone print HTML and is never mounted by React.
function MetricsTable(props: {metrics: StockTakeReportMetric[]; isMetadata?: boolean}) {
  return (
    <table data-document-metadata={props.isMetadata || undefined}>
      <tbody>
        {props.metrics.map((metric) => (
          <tr key={metric.id}>
            <th scope="row">
              {metric.label}
              <small>{metric.tooltip}</small>
            </th>
            <td>
              {metric.value}
              <small>{metric.note}</small>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function createStockTakeReportDocument(input: {
  report: StockTakeReport;
  generatedAt: string;
  generatedLabel: string;
  locale: string;
}) {
  return `<!doctype html>${renderToStaticMarkup(
    <html lang={input.locale}>
      <head>
        <meta charSet="utf-8" />
        <title>{input.report.title}</title>
        <style>{DOCUMENT_STYLES}</style>
      </head>
      <body data-report-kind={input.report.kind}>
        <header>OMNETIC</header>
        <main>
          <h1>{input.report.title}</h1>
          <p>{`${input.generatedLabel} ${input.generatedAt}`}</p>
          <MetricsTable metrics={input.report.metadata} isMetadata />
          {input.report.sections.map((section) => (
            <section key={section.id} aria-label={section.title}>
              <h2>{section.title}</h2>
              <div data-report-groups>
                {section.groups.map((group) => (
                  <article
                    key={group.id}
                    aria-label={group.title}
                    data-report-group
                    data-group={group.id}
                  >
                    <h3>{group.title}</h3>
                    <MetricsTable metrics={group.metrics} />
                  </article>
                ))}
              </div>
            </section>
          ))}
        </main>
        <footer>
          {input.report.notes?.map((note) => <p key={note}>{note}</p>)}
          <small>{`Omnetic · ${input.report.title}`}</small>
        </footer>
      </body>
    </html>
  )}`;
}
